// API constants
export const API_BASE = 'https://consumet-olive-five.vercel.app/movies/flixhq';
export const TMDB_API_BASE = 'https://api.themoviedb.org/3';
export const TMDB_API_KEY = '86da74c5c11f2b69a4ce724f254ac312';
export const ANILIST_API = 'https://graphql.anilist.co';
export const JIKAN_API = 'https://api.jikan.moe/v4';
export const ANIBHINDI_API = 'https://ani-biee-anime-world-india-scraper-gamma.vercel.app';
export const EMBEDMASTER = 'https://embedmaster.link';
export const VIXSRC_API = 'https://povertyscraper-8e5y.onrender.com';
export const POVSCRAPER = 'https://povscraper-xopy.onrender.com';
export const VIDEASY_API = 'https://videasyscr.onrender.com';
export const EP_RANGE_SIZE = 50;
export const TODAY = new Date();

export const isUpcoming = (rawDate) => rawDate && new Date(rawDate) > TODAY;

// Fetch with retry (handles 429 rate limiting)
export const fetchWithRetry = async (url, options = {}, retries = 3, baseDelay = 1000) => {
  for (let attempt = 0; attempt <= retries; attempt++) {
    const res = await fetch(url, options);
    if (res.status === 429 && attempt < retries) {
      const retryAfter = res.headers.get('Retry-After');
      const delay = retryAfter ? parseInt(retryAfter) * 1000 : baseDelay * Math.pow(2, attempt);
      await new Promise(r => setTimeout(r, delay));
      continue;
    }
    return res;
  }
};

export const anilistFetch = async (query, variables = {}) => {
  const res = await fetch(ANILIST_API, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ query, variables }),
  });
  if (!res.ok) throw new Error(`AniList HTTP ${res.status}`);
  return res.json();
};

// localStorage helpers
const CW_KEY = 'continueWatching';
const CW_ANIME_KEY = 'continueWatchingAnime';
const WL_KEY = 'watchlist';
const WL_ANIME_KEY = 'watchlistAnime';

export const getCW = (isAnime = false) => {
  const key = isAnime ? CW_ANIME_KEY : CW_KEY;
  try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch { return []; }
};

export const saveCW = (items, isAnime = false) => {
  const key = isAnime ? CW_ANIME_KEY : CW_KEY;
  try { localStorage.setItem(key, JSON.stringify(items.slice(0, 12))); } catch {}
};

export const getWatchlist = (isAnime = false) => {
  const key = isAnime ? WL_ANIME_KEY : WL_KEY;
  try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch { return []; }
};

export const saveWatchlist = (items, isAnime = false) => {
  const key = isAnime ? WL_ANIME_KEY : WL_KEY;
  try { localStorage.setItem(key, JSON.stringify(items.slice(0, 50))); } catch {}
};

export const isInWatchlist = (id, isAnime = false) => {
  return getWatchlist(isAnime).some(i => i.id === id);
};

export const toggleWatchlistItem = (item, isAnime = false) => {
  const wl = getWatchlist(isAnime);
  const exists = wl.findIndex(i => i.id === item.id);
  if (exists >= 0) {
    wl.splice(exists, 1);
  } else {
    wl.unshift(item);
  }
  saveWatchlist(wl, isAnime);
  return wl;
};

// AniList queries
export const Q_TRENDING = `query($page:Int){Page(page:$page,perPage:24){media(type:ANIME,sort:TRENDING_DESC,isAdult:false){id title{romaji english}coverImage{extraLarge large}episodes averageScore format status seasonYear genres}}}`;
export const Q_POPULAR = `query{Page(perPage:24){media(type:ANIME,sort:POPULARITY_DESC,isAdult:false,status_in:[RELEASING,FINISHED]){id title{romaji english}coverImage{extraLarge large}episodes averageScore format status seasonYear genres}}}`;
export const Q_PREVIEW = `query($search:String){Page(perPage:5){media(search:$search,type:ANIME,isAdult:false){id title{romaji english}coverImage{large}format averageScore seasonYear}}}`;
export const Q_SEARCH = `query($search:String,$page:Int){Page(page:$page,perPage:50){media(search:$search,type:ANIME,isAdult:false,sort:SEARCH_MATCH){id title{romaji english}coverImage{extraLarge large}episodes averageScore format status seasonYear genres}}}`;
export const Q_DETAILS = `query($id:Int){Media(id:$id,type:ANIME){id title{romaji english native}description(asHtml:false)coverImage{extraLarge large}bannerImage episodes averageScore format status seasonYear genres idMal studios(isMain:true){nodes{name}}nextAiringEpisode{episode}streamingEpisodes{title thumbnail url site}}}`;

export const mapAnilistItem = m => ({
  id: m.id,
  title: m.title.english || m.title.romaji,
  image: m.coverImage?.extraLarge || m.coverImage?.large,
  rating: m.averageScore,
  format: m.format,
  releaseDate: m.seasonYear,
  episodes: m.episodes,
  genres: m.genres || [],
});

// Search cache
const searchCache = new Map();
const CACHE_TTL = 10 * 60 * 1000;

const getCachedSearch = (key) => {
  const e = searchCache.get(key);
  if (!e) return null;
  if (Date.now() - e.ts > CACHE_TTL) { searchCache.delete(key); return null; }
  return e.data;
};

const setCachedSearch = (key, data) => searchCache.set(key, { data, ts: Date.now() });

export const tmdbDetailsCache = new Map();

export const tmdbSearch = async (query, limit = 50) => {
  const key = `tmdb:${query}:${limit}`;
  const hit = getCachedSearch(key);
  if (hit) return hit;
  const res = await fetchWithRetry(
    `${TMDB_API_BASE}/search/multi?api_key=${TMDB_API_KEY}&query=${encodeURIComponent(query)}&page=1&include_adult=false`
  );
  if (!res.ok) throw new Error(`TMDB ${res.status}`);
  const data = await res.json();
  const results = (data.results || [])
    .filter(r => r.media_type === 'movie' || r.media_type === 'tv')
    .slice(0, limit)
    .map(r => ({
      id: `tmdb-${r.media_type}-${r.id}`,
      title: r.title || r.name,
      image: r.poster_path ? `https://image.tmdb.org/t/p/w342${r.poster_path}` : null,
      type: r.media_type === 'movie' ? 'Movie' : 'TV Series',
      releaseDate: (r.release_date || r.first_air_date)?.split('-')[0],
      _rawDate: r.release_date || r.first_air_date,
      rating: r.vote_average,
      isImdbId: false,
      tmdbId: r.id,
      mediaType: r.media_type,
    }));
  setCachedSearch(key, results);
  return results;
};

// Genre list for filtering
export const MOVIE_GENRES = [
  { id: 28, name: 'Action' }, { id: 12, name: 'Adventure' }, { id: 16, name: 'Animation' },
  { id: 35, name: 'Comedy' }, { id: 80, name: 'Crime' }, { id: 18, name: 'Drama' },
  { id: 14, name: 'Fantasy' }, { id: 27, name: 'Horror' }, { id: 10749, name: 'Romance' },
  { id: 878, name: 'Sci-Fi' }, { id: 53, name: 'Thriller' }, { id: 10752, name: 'War' },
];
